/**
 * Helpers for calendar-day-only values.
 *
 * A date-only value is exchanged with the server as "yyyy-MM-dd" (or as an ISO string whose first
 * ten characters are that calendar day). Building a local Date from the string's date part keeps the
 * same day in every browser timezone, which `new Date(isoString)` does not: the server stores these
 * as UTC midnight, so a Central-time browser would otherwise render the previous day.
 */

/** Formats a picked date as "yyyy-MM-dd" using its local calendar day. */
export function toDateOnlyString(value: Date | string | null | undefined): string | null {
  if (!value) return null;

  const date = typeof value === "string" ? parseDateOnly(value) : value;
  if (!date || isNaN(date.getTime())) return null;

  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Parses a date-only value into a local Date at midnight, without timezone drift. */
export function parseDateOnly(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}
